// =====================================================
// Shared: Nav, Footer, common widgets
//
// Phase-14.5 C4: dropped @ts-nocheck. Components were the priority
// (Nav + dropdowns drive every page); the SVG helpers further down
// (Sparkline, CognitiveRadar, KnowledgeGraph, ArchStack) got
// function-signature typing but not deep type-safety on every SVG
// path expression — that's pure-render math, not RBAC-critical.
// =====================================================
import React from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "./icons";
// S-I18N-W1a — locale-aware digits + the non-hook translator seam.
import { toFaDigits } from "./i18n/jalali";
import { formatLocaleDate, formatLocaleRelativeTime } from "./i18n/intl";
import { BidiInline } from "./i18n/BidiText";
import { getLocale, translate } from "./i18n/locale-state";
import { LanguageSwitcher } from "./components/LanguageSwitcher";
import { useLocale } from "./i18n/LocaleProvider";
import { useRole, ROLES, type RoleId, type Role } from "./role";
import { useAuth, type AuthContextValue } from "./auth/AuthContext";
import type { Actions, Subjects } from "./auth/ability";
import { Button } from "./ui";
import { Avatar } from "./ui/Avatar";
import type { Go } from "./router";
import { OrgAttribution } from "./components/OrgAttribution";
import { TrustSeals } from "./landing/trust-seals";
import { BRAND, RIGHTS_HOLDER_FA, brandNameForLocale } from "./brand/brand.config";
import { useNotifications, notifLink, type UseNotifications } from "./hooks/useNotifications";
import { useConnectSummary } from "./hooks/useConnectSummary";
import { useChatUnread } from "./hooks/useChatUnread";
// S-MKT-PRO-W9 — the shared cart store (module-level; the same instance the market pages use).
import { useCart } from "./pages/market/cart-state";
import { useTerm } from "./term-context";

// (Window.toast / Window.confirmAction / Window.openCommandPalette
// types live in ui.tsx — the file that actually assigns them. Both
// declarations would merge if duplicated; keeping a single source.)

interface NavItem {
  id: string;
  label: string;
  /** Marks routes that hit the real API; renders the pulse dot. */
  live?: boolean;
  /**
   * R-NAV-1 — optional CASL gate. When present, the item only renders if
   * the current user's ability allows `action` on `subject`. This is the
   * "filter nav by ability.can" wiring (spec §2.7-4); the full per-item
   * tagging of the redesigned sidebar lands in R-NAV-2.
   */
  can?: { action: Actions; subject: Subjects };
}

// R-NAV-1 (spec §3.1.3, §2.5) — anonymous visitors see PUBLIC nav links,
// never a role's workspace links (which would bounce to /login and leak a
// "logged-in" feel). Only routes classified PUBLIC are listed so no link
// dead-ends at the auth gate. (Catalog becomes public in R-NAV-3; it
// joins this list then.)
const PUBLIC_NAV_ITEMS: NavItem[] = [
  { id: "programs", label: "topnav.public.programs" },
  // R-MARKET P4 — «بازار دانش» PUBLIC marketplace. Reachable LOGGED-OUT (doc §7.1:
  // every role + a guest can browse), so it belongs in the guest/public top nav.
  // route-classification marks /market PUBLIC, so this link never dead-ends at
  // the auth gate. `live` (it talks to the public catalog API).
  { id: "market", label: "topnav.public.market", live: true },
  // «درخواست پذیرش» → the real public /apply admissions funnel (live API). Surfaced
  // as a first-class public nav link (not just the «ثبت‌نام» CTA button) so an
  // anonymous visitor can discover admissions directly. /apply is classified PUBLIC,
  // so the link never bounces to the auth gate.
  { id: "apply", label: "topnav.public.apply", live: true },
  { id: "about", label: "topnav.public.about" },
  { id: "pricing", label: "topnav.public.pricing" },
  { id: "help", label: "topnav.public.help" },
];

// Nav highlights the LIVE routes that actually talk to the API (Phase 4-9).
// Mocked routes from the original SPA still hash-route alongside but are
// no longer the default landing surface for any role.
const NAV_ITEMS_BY_ROLE: Record<RoleId, NavItem[]> = {
  finance_manager: [
    { id: "admin/finance/treasury", label: "topnav.finance_manager.admin-finance-treasury", live: true },
    { id: "admin/finance/payroll", label: "topnav.finance_manager.admin-finance-payroll", live: true },
    { id: "admin/finance/decrees", label: "topnav.finance_manager.admin-finance-decrees", live: true },
    { id: "admin/finance/claims", label: "topnav.finance_manager.admin-finance-claims", live: true },
  ],
  student: [
    { id: "progress", label: "topnav.student.progress", live: true },
    { id: "catalog", label: "topnav.student.catalog", live: true },
    { id: "my-courses", label: "topnav.student.my-courses", live: true },
    { id: "tutor", label: "topnav.student.tutor", live: true },
    { id: "calendar", label: "topnav.student.calendar" },
    { id: "community", label: "topnav.student.community" },
  ],
  instructor: [
    { id: "progress", label: "topnav.instructor.progress", live: true },
    { id: "my-courses", label: "topnav.instructor.my-courses", live: true },
    { id: "admin/course-content", label: "topnav.instructor.admin-course-content" },
    { id: "gradebook", label: "topnav.instructor.gradebook", live: true },
    { id: "classroom", label: "topnav.instructor.classroom" },
    { id: "messages", label: "topnav.instructor.messages" },
    { id: "tutor", label: "topnav.instructor.tutor", live: true },
  ],
  admin: [
    { id: "progress", label: "topnav.admin.progress", live: true },
    { id: "admin", label: "topnav.admin.admin" },
    { id: "analytics", label: "topnav.admin.analytics" },
    { id: "schools", label: "topnav.admin.schools" },
    { id: "events", label: "topnav.admin.events" },
    { id: "tutor", label: "topnav.admin.tutor", live: true },
  ],
  // R-NAV-2 — faculty_admin (scope-bound). Links to the academic surfaces
  // they manage within their own faculty.
  faculty_admin: [
    { id: "faculty-dashboard", label: "topnav.faculty_admin.faculty-dashboard" },
    { id: "admin/departments", label: "topnav.faculty_admin.admin-departments" },
    { id: "admin/programs", label: "topnav.faculty_admin.admin-programs" },
    { id: "admin/offerings", label: "topnav.faculty_admin.admin-offerings" },
    { id: "admin/enrollments", label: "topnav.faculty_admin.admin-enrollments" },
    { id: "messages", label: "topnav.faculty_admin.messages" },
  ],
  // Parent role: lands on the parent dashboard first. The public "home"
  // route was the first item which felt jarring for a logged-in parent;
  // now we lead with the parent dashboard and drop "home" — parents
  // reach the public site via the logo in the brand area if needed.
  parent: [
    { id: "parent", label: "topnav.parent.parent" },
    { id: "calendar", label: "topnav.parent.calendar" },
    { id: "credential", label: "topnav.parent.credential" },
    { id: "messages", label: "topnav.parent.messages" },
    { id: "help", label: "topnav.parent.help" },
  ],
  org: [
    { id: "org", label: "topnav.org.org" },
    { id: "analytics", label: "topnav.org.analytics" },
    { id: "faculty", label: "topnav.org.faculty" },
    { id: "events", label: "topnav.org.events" },
    { id: "pricing", label: "topnav.org.pricing" },
    { id: "help", label: "topnav.org.help" },
  ],
  // Phase-15 R7: nav for the 5 roles added in R1's seed.
  ta: [
    { id: "classroom", label: "topnav.ta.classroom", live: true },
    { id: "my-courses", label: "topnav.ta.my-courses", live: true },
    { id: "progress", label: "topnav.ta.progress", live: true },
    { id: "tutor", label: "topnav.ta.tutor", live: true },
  ],
  content_manager: [
    { id: "catalog", label: "topnav.content_manager.catalog", live: true },
    { id: "admin/course-content", label: "topnav.content_manager.admin-course-content" },
    { id: "analytics", label: "topnav.content_manager.analytics" },
    { id: "messages", label: "topnav.content_manager.messages" },
    { id: "help", label: "topnav.content_manager.help" },
  ],
  support: [
    { id: "support", label: "topnav.support.support" },
    { id: "audit", label: "topnav.support.audit", live: true, can: { action: "read", subject: "AuditLog" } },
    { id: "messages", label: "topnav.support.messages" },
    { id: "help", label: "topnav.support.help" },
  ],
  moderator: [
    { id: "community", label: "topnav.moderator.community" },
    { id: "messages", label: "topnav.moderator.messages" },
    { id: "help", label: "topnav.moderator.help" },
  ],
  super_admin: [
    { id: "progress", label: "topnav.super_admin.progress", live: true },
    { id: "admin", label: "topnav.super_admin.admin" },
    { id: "audit", label: "topnav.super_admin.audit", live: true, can: { action: "read", subject: "AuditLog" } },
    { id: "analytics", label: "topnav.super_admin.analytics" },
    { id: "tutor", label: "topnav.super_admin.tutor", live: true },
  ],
  // WELL-4 — counselor (مشاور دانشگاه). Least-privilege wellness desk only.
  counselor: [
    { id: "counselor-inbox", label: "topnav.counselor.counselor-inbox" },
    { id: "counseling-appointments", label: "topnav.counselor.counseling-appointments" },
    { id: "crisis-queue", label: "topnav.counselor.crisis-queue" },
    { id: "messages", label: "topnav.counselor.messages" },
  ],
  // S-CARD فاز A W1 — میزِ عضویت. کنسولش در W5 می‌آید؛ تا آن‌وقت فقط «حساب».
  membership_operator: [{ id: "profile", label: "topnav.membership_operator.profile" }],
  membership_manager: [{ id: "profile", label: "topnav.membership_manager.profile" }],
  assessor: [{ id: "profile", label: "topnav.assessor.profile" }],
  assessment_manager: [{ id: "profile", label: "topnav.assessment_manager.profile" }],
};

export type NavMode = "public" | "workspace" | "auth_flow";

export interface NavProps {
  current: string;
  go: Go;
  /**
   * Phase-A R1.1: route-kind-aware mode set by AppShell.
   *   public    — top-nav links + own hamburger drawer for those links
   *               (existing behavior; default)
   *   workspace — top-nav links hidden; the single hamburger calls
   *               `onWorkspaceMenuClick` which AppShell wires to its
   *               sidebar Sheet drawer
   *   auth_flow — minimal chrome: brand + theme toggle only; no nav
   *               links, no hamburger, no notifications/user popovers
   */
  mode?: NavMode;
  /** Workspace-only: AppShell-provided callback to open the sidebar drawer. */
  onWorkspaceMenuClick?: () => void;
  /**
   * Workspace-only: whether the sidebar Sheet drawer is currently open.
   * Wired to `aria-expanded` on the hamburger trigger (R7.5). Replaces
   * the earlier `aria-controls="appshell-sidebar-drawer"` which axe-core
   * flagged as `aria-valid-attr-value` on every workspace route (the
   * referenced id wasn't in the DOM because Radix Sheet lazy-mounts).
   */
  workspaceMenuOpen?: boolean;
}

// R-SIS-3d (§3.1.5) — roles that see the navbar term switcher (academic ones).
const ACADEMIC_TERM_ROLES = new Set<RoleId>([
  "student",
  "instructor",
  "faculty_admin",
  "ta",
  "admin",
  "super_admin",
]);

export const Nav = ({
  current,
  go,
  mode = "public",
  onWorkspaceMenuClick,
  workspaceMenuOpen = false,
}: NavProps): React.ReactElement => {
  const { t: tShell } = useTranslation("shell");
  const { role, setRole, availableRoles } = useRole();
  const auth = useAuth();
  // The central brand resolver selects governed fa/ar names and the official
  // English name for every other registered UI locale.
  const { locale } = useLocale();
  const brandNameFull = brandNameForLocale(locale);
  // R-NAV-5 — real notifications (bell badge + dropdown). No-op for anon.
  const notif = useNotifications({ limit: 10 });
  // R-CONNECT — pending incoming connection requests → «شبکه» nav badge. No-op
  // for anon. Mounted once for the whole authed session (the Nav is too).
  const connect = useConnectSummary();
  // R-CHAT M4 — total unread chat messages → «پیام‌ها» nav badge. No-op for anon;
  // polls ~15s + live-bumps on socket "message".
  const chatUnread = useChatUnread();
  // S-MKT-PRO-W9 — the buyer's cart line-count → «سبد خرید» nav badge. No-op for anon;
  // the shared store fetches once per session and every add/remove reconciles it.
  const cart = useCart(!!auth.user);
  // R-SIS-3d (§3.1.5) — term switcher: academic roles only, workspace + authed.
  const { terms, selectedTermId, setSelectedTermId } = useTerm();
  const showTermSwitcher =
    !!auth.user &&
    mode === "workspace" &&
    terms.length > 0 &&
    ACADEMIC_TERM_ROLES.has(role.id);
  const [open, setOpen] = React.useState(false);
  const [notifsOpen, setNotifsOpen] = React.useState(false);
  const [userOpen, setUserOpen] = React.useState(false);
  React.useEffect(() => { setOpen(false); setNotifsOpen(false); setUserOpen(false); }, [current]);
  const isWorkspace = mode === "workspace";
  const isAuthFlow = mode === "auth_flow";

  // Logo destination is mode + auth aware. WORKSPACE always → dashboard;
  // PUBLIC: dashboard if logged in (back-to-workspace shortcut), else home;
  // AUTH_FLOW: home (escape back to the marketing site).
  const brandTarget: string = isWorkspace
    ? "dashboard"
    : isAuthFlow
      ? "home"
      : auth.isAuthenticated
        ? "dashboard"
        : "home";

  // close popovers on outside click
  React.useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target?.closest(".notif-wrap")) setNotifsOpen(false);
      if (!target?.closest(".user-wrap")) setUserOpen(false);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  // Esc closes the mobile drawer + any popover.
  React.useEffect(() => {
    if (!open && !notifsOpen && !userOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        setNotifsOpen(false);
        setUserOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, notifsOpen, userOpen]);

  // Body-scroll lock while the mobile drawer is open. Without this the
  // page below can be scrolled with two-finger gestures while the drawer
  // is visible, which feels broken on iOS Safari.
  React.useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.classList.toggle("nav-drawer-open", open);
    return () => document.body.classList.remove("nav-drawer-open");
  }, [open]);

  // R-NAV-1: authed → the active role's links, CASL-filtered; anonymous →
  // public links only (no role workspace leak). role.id === activeRoleId
  // for an authed user (role derives from the session now).
  const items: NavItem[] = auth.isAuthenticated
    ? (NAV_ITEMS_BY_ROLE[role.id] || NAV_ITEMS_BY_ROLE.student).filter(
        (n) => !n.can || auth.ability.can(n.can.action, n.can.subject),
      )
    : PUBLIC_NAV_ITEMS;

  return (
    <nav className="nav" data-mode={mode}>
      <div className="nav-inner">
        {/* Phase-A R6.6 — RTL navbar correctness:
            WORKSPACE renders the hamburger as the FIRST child of
            nav-inner so under RTL it sits at the START edge (right in
            Persian), matching the standard Persian sidebar pattern
            (menu opener on the right, logo next to it, user/theme/
            notification chips on the left). The PUBLIC mode keeps the
            hamburger inside `.nav-actions` at the end (left in RTL)
            because that's the established mobile pattern there — the
            user is opening a drawer of nav-links that fly out from the
            same edge as the brand. AUTH_FLOW has no hamburger. */}
        {isWorkspace ? (
          <button
            id="appshell-sidebar-trigger"
            type="button"
            className="nav-toggle nav-toggle-start"
            onClick={() => onWorkspaceMenuClick?.()}
            aria-label={tShell("nav.aria.menu")}
            // R7.5 — disclosure-widget ARIA pattern:
            // aria-controls dropped because Radix Sheet lazy-mounts the
            // drawer, so the IDREF can't always resolve (axe-core
            // aria-valid-attr-value rule fired on all 53 workspace
            // routes). aria-expanded + aria-haspopup="dialog" is the
            // canonical pattern recommended by ARIA Authoring Practices
            // for buttons that open a dialog/menu — and it's the pattern
            // Radix wires automatically when you use <SheetTrigger>,
            // which we can't here because the trigger lives in Nav
            // (rendered by AppShell at a different tree depth than the
            // Sheet itself).
            aria-expanded={workspaceMenuOpen}
            aria-haspopup="dialog"
          >
            <span className="bars"></span>
          </button>
        ) : null}

        <a href={"/" + brandTarget} onClick={(e) => { e.preventDefault(); go(brandTarget); }} className="brand">
          {/* S-REBRAND-EMI موج ۳ — نشانِ دانشگاه جایگزینِ لوگوی کوبرندِ قبلی
              شد (تصمیمِ مالک: حذفِ کامل کوبرند؛ مجریِ فنی فقط در فوتر). نوارِ
              بالا روشن است، پس نسخهٔ on-light (سرمه‌ای/طلایی) می‌نشیند. */}
          <img
            src={BRAND.assets.markOnLight96}
            alt={brandNameFull}
            width="40"
            height="40"
            style={{ width: 40, height: 40, objectFit: "contain", display: "block" }}
          />
          <span>
            {brandNameFull}
            <div className="brand-sub">{tShell("brand.markSubtitle")}</div>
          </span>
        </a>

        {/* Top-nav links: PUBLIC only. WORKSPACE relies on the sidebar
            for in-app navigation, AUTH_FLOW shows a minimal shell. */}
        {mode === "public" && (
          <div className={"nav-links " + (open ? "open" : "")}>
            {items.map((n) => (
              <a
                key={n.id}
                href={"#" + n.id}
                onClick={(e) => { e.preventDefault(); go(n.id); setOpen(false); }}
                className={"nav-link " + (current === n.id ? "active" : "")}
                aria-current={current === n.id ? "page" : undefined}
              >
                {tShell(n.label)}
                {n.live && (
                  <span
                    className="nav-live-dot"
                    aria-label={tShell("nav.liveDot.aria")}
                    title={tShell("nav.liveDot.title")}
                  />
                )}
              </a>
            ))}
          </div>
        )}

        <div className="nav-actions">
          {/* R-SIS-3d (§3.1.5) — term switcher (academic roles, workspace).
              Registration always uses the ACTIVE term server-side; the
              selection drives term-aware views (transcript, schedule). */}
          {showTermSwitcher && (
            <select
              className="nav-term-switcher hide-on-xs"
              value={selectedTermId ?? ""}
              onChange={(e) => setSelectedTermId(e.target.value)}
              aria-label={tShell("nav.term.aria")}
              title={tShell("nav.term.title")}
              style={{
                fontFamily: "inherit",
                fontSize: 13,
                padding: "6px 10px",
                borderRadius: 10,
                background: "var(--surface)",
                color: "var(--fg)",
                border: "1px solid var(--line-2)",
                maxWidth: 180,
              }}
            >
              {terms.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title}
                  {t.status === "ACTIVE" ? " ●" : ""}
                </option>
              ))}
            </select>
          )}

          {/* Command palette trigger.
              Phase-16 Gate-2 smoke: hidden at <sm because the 5-icon
              cluster + brand exceeds 320 px after R7' enforced 44 px
              touch targets. Keyboard users still get it via Ctrl/Cmd+K;
              touch users find it inside the hamburger drawer. */}
          <button
            className="nav-icon-btn hide-on-xs"
            onClick={() => window.openCommandPalette?.()}
            aria-label={tShell("nav.cmdk.aria")}
            title={tShell("nav.cmdk.title")}
          >
            <Icon name="search" size={18} />
          </button>

          {/* Theme toggle removed (D-2026-06-13, owner): dark mode is disabled
              for now — the site is forced light in main.tsx. Re-add this button
              when dark returns. */}

          {/* R-CONNECT — «شبکه» quick-access with a pendingIncoming badge.
              Authed only (no connect state for a guest). Clicking opens the
              network hub (/network). Hidden at <xs for the same 320-px width
              constraint as the other icon chips. */}
          {auth.user && (
            <button
              className="nav-icon-btn hide-on-xs"
              style={{ position: "relative" }}
              onClick={() => go("network")}
              aria-label={
                connect.pendingIncoming > 0
                  ? tShell("nav.connect.ariaPending", { count: connect.pendingIncoming })
                  : tShell("nav.connect.label")
              }
              title={tShell("nav.connect.title")}
            >
              <Icon name="users" size={18} />
              {connect.pendingIncoming > 0 && (
                <span
                  aria-hidden="true"
                  style={{
                    position: "absolute", top: 0, insetInlineEnd: 0,
                    minWidth: 16, height: 16, padding: "0 4px",
                    borderRadius: 8, background: "var(--accent)", color: "#fff",
                    fontSize: 9, fontWeight: 700, lineHeight: "16px", textAlign: "center",
                  }}
                >
                  {connect.pendingIncoming > 99 ? `${toFa(99)}+` : toFa(connect.pendingIncoming)}
                </span>
              )}
            </button>
          )}

          {/* R-CHAT M4 — «پیام‌ها» quick-access with an unread badge. Authed only;
              clicking opens the messenger (/messages). Hidden at <xs for the same
              320-px width constraint as the other icon chips. */}
          {auth.user && (
            <button
              className="nav-icon-btn hide-on-xs"
              style={{ position: "relative" }}
              onClick={() => go("messages")}
              aria-label={
                chatUnread.totalUnread > 0
                  ? tShell("nav.chat.ariaUnread", { count: chatUnread.totalUnread })
                  : tShell("nav.chat.label")
              }
              title={tShell("nav.chat.label")}
            >
              <Icon name="chat" size={18} />
              {chatUnread.totalUnread > 0 && (
                <span
                  aria-hidden="true"
                  style={{
                    position: "absolute", top: 0, insetInlineEnd: 0,
                    minWidth: 16, height: 16, padding: "0 4px",
                    borderRadius: 8, background: "var(--accent)", color: "#fff",
                    fontSize: 9, fontWeight: 700, lineHeight: "16px", textAlign: "center",
                  }}
                >
                  {chatUnread.totalUnread > 99 ? `${toFa(99)}+` : toFa(chatUnread.totalUnread)}
                </span>
              )}
            </button>
          )}

          {/* S-MKT-PRO-W9 — «سبد خرید» quick-access with a line-count badge. Authed only;
              clicking opens /market/cart. The count comes from the SHARED cart store (one
              fetch per SPA session, deduped), so adding a course anywhere on the site
              updates this badge immediately. Hidden at <xs like the other icon chips, and
              hidden entirely when the cart is empty (no chrome for an empty cart). */}
          {auth.user && cart.count > 0 && (
            <button
              className="nav-icon-btn hide-on-xs"
              style={{ position: "relative" }}
              onClick={() => go("market/cart")}
              aria-label={tShell("nav.cart.aria", { count: cart.count })}
              title={tShell("nav.cart.label")}
            >
              <Icon name="cart" size={18} />
              <span
                aria-hidden="true"
                style={{
                  position: "absolute", top: 0, insetInlineEnd: 0,
                  minWidth: 16, height: 16, padding: "0 4px",
                  borderRadius: 8, background: "var(--accent)", color: "#fff",
                  fontSize: 9, fontWeight: 700, lineHeight: "16px", textAlign: "center",
                }}
              >
                {cart.count > 99 ? `${toFa(99)}+` : toFa(cart.count)}
              </span>
            </button>
          )}

          {/* Notifications — authed only. R-NAV-1 (spec §2.5/§3.1):
              an anonymous visitor sees no bell (no notifications without a
              session). R-NAV-5 wires this to the real /v1/notifications.
              Phase-16 Gate-2 smoke: also `.hide-on-xs` for the same
              320-px width constraint. */}
          {auth.user && (
            <div className="notif-wrap relative hide-on-xs" >
              <button
                className="nav-icon-btn"
                style={{ position: "relative" }}
                onClick={() => setNotifsOpen(!notifsOpen)}
                aria-label={notif.unreadCount > 0 ? tShell("nav.notif.aria", { count: notif.unreadCount }) : tShell("nav.notif.label")}
              >
                <Icon name="bell" size={18} />
                {notif.unreadCount > 0 && (
                  <span
                    aria-hidden="true"
                    style={{
                      position: "absolute", top: 0, insetInlineEnd: 0,
                      minWidth: 16, height: 16, padding: "0 4px",
                      borderRadius: 8, background: "var(--accent)", color: "#fff",
                      fontSize: 9, fontWeight: 700, lineHeight: "16px", textAlign: "center",
                    }}
                  >
                    {notif.unreadCount > 99 ? `${toFa(99)}+` : toFa(notif.unreadCount)}
                  </span>
                )}
              </button>
              {notifsOpen && (
                <NotificationsDropdown go={go} notif={notif} onClose={() => setNotifsOpen(false)} />
              )}
            </div>
          )}

          {/* S-I18N-W1a — the language switcher sits just before the user
              button so it is reachable in every mode, including for a guest
              who has no user menu at all (ADR-A3: guests persist the choice
              in the `du_locale` cookie, accounts in `User.locale`). Compact
              on the public bar and in AUTH_FLOW (login/register — the switch
              sits above the card there, S-I18N-W1a-FIX), full inside the
              workspace chrome. */}
          <LanguageSwitcher
            authed={!!auth.user}
            enabledLocales={auth.user?.enabledLocales}
            variant={isWorkspace ? "full" : "compact"}
          />

          {/* R-NAV-1 (spec §2.5, §3.1.13) — authed: avatar + name + user
              menu. Anonymous: «ورود» / «ثبت‌نام» buttons, NO user menu, NO
              role identity. This is the core fix for "logout looks
              logged-in": an anonymous visitor can no longer see a role
              avatar/name/menu derived from the default student role. */}
          {auth.user ? (
            <div className="user-wrap relative" >
              <button
                className="user-btn"
                onClick={() => setUserOpen(!userOpen)}
                aria-label={tShell("nav.aria.userMenu")}
              >
                <Avatar
                  seed={auth.user.userId}
                  role={role.id}
                  name={auth.user.fullName || auth.user.email}
                  size={32}
                />
                <span className="user-name">{
                  auth.user.fullName?.split(" ")[0]
                    || auth.user.email?.split("@")[0]
                    || role.label
                }</span>
              </button>
              {userOpen && (
                <UserDropdown go={go} role={role} setRole={setRole} auth={auth} availableRoles={availableRoles} />
              )}
            </div>
          ) : (
            // Anonymous CTA. Hidden in AUTH_FLOW (you're already on
            // login/register — redundant). «ورود» is intentionally NOT
            // "ورود به حساب" (the form-submit string) to avoid colliding
            // with the Playwright login helper's button matcher.
            !isAuthFlow && (
              <div className="auth-cta flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => go("login")}>
                  {tShell("auth.login")}
                </Button>
                {/* R-AUTH-1: application-only — «ثبت‌نام» now leads to the
                    real /apply funnel (open self-registration retired). */}
                <Button variant="primary" size="sm" onClick={() => go("apply")}>
                  {tShell("auth.register")}
                </Button>
              </div>
            )
          )}

          {/* Hamburger button — PUBLIC mode only.
              WORKSPACE renders its own hamburger at the start of
              nav-inner (per R6.6). AUTH_FLOW has no hamburger. */}
          {!isAuthFlow && !isWorkspace && (
            <button
              type="button"
              className="nav-toggle"
              onClick={() => setOpen(!open)}
              aria-label={tShell("nav.aria.menu")}
              aria-expanded={open ? "true" : "false"}
            >
              <span className="bars"></span>
            </button>
          )}
        </div>
      </div>
    </nav>
  );
};

interface NotificationsDropdownProps {
  go: Go;
  notif: UseNotifications;
  onClose: () => void;
}
// R-NAV-5 (spec §3.1.7, §18.1) — the bell dropdown bound to the REAL inbox
// (was 5 hardcoded rows). Clicking a notification marks it read + navigates to
// its mapped destination; «علامت همه خوانده» calls the real read-all endpoint.
const NotificationsDropdown = ({ go, notif, onClose }: NotificationsDropdownProps): React.ReactElement => {
  const { t: tShell } = useTranslation("shell");
  const { items, unreadCount, loading, error, markRead, markAllRead } = notif;
  const onItemClick = (n: { id: string; type: string; read: boolean }): void => {
    if (!n.read) void markRead(n.id);
    const link = notifLink(n.type);
    onClose();
    if (link) go(link);
  };
  return (
    <div className="dropdown" style={{ width: 380 }}>
      <div className="dropdown-head">
        <h4>{tShell("nav.notif.title")}</h4>
        <span style={{ fontSize: 11, color: "var(--fg-mute)", fontFamily: "var(--f-mono)" }}>
          {unreadCount > 0 ? tShell("nav.notif.unreadCount", { count: unreadCount }) : tShell("nav.notif.allRead")}
        </span>
      </div>
      <div className="overflow-y-auto" style={{ maxHeight: 420 }}>
        {loading ? (
          <div style={{ padding: 24, textAlign: "center", color: "var(--fg-mute)", fontSize: 13 }}>
            {tShell("common:state.loading")}
          </div>
        ) : error ? (
          <div style={{ padding: 24, textAlign: "center", color: "var(--fg-mute)", fontSize: 13 }}>
            {tShell("nav.notif.loadFailed")}
          </div>
        ) : items.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center", color: "var(--fg-mute)", fontSize: 13 }}>
            {tShell("nav.notif.empty")}
          </div>
        ) : (
          items.map((n) => (
            // a11y: a real <button> so the row is keyboard-focusable + Enter/Space
            // activatable (was a click-only <div>). Reset button chrome; the
            // notif-item class supplies the flex layout.
            <button
              type="button"
              key={n.id}
              onClick={() => onItemClick(n)}
              className="notif-item"
              style={{ cursor: "pointer", width: "100%", display: "flex", textAlign: "start", background: "none", border: "none", font: "inherit", color: "inherit" }}
            >
              <div className="notif-icon"><Icon name="bell" size={14} /></div>
              <div className="flex-1" style={{ minWidth: 0 }}>
                <div className="flex items-center gap-2 mb-1">
                  <BidiInline style={{ fontSize: 13, fontWeight: 600 }}>{n.title}</BidiInline>
                  {!n.read && <span style={{ width: 6, height: 6, borderRadius: 50, background: "var(--accent)" }}></span>}
                </div>
                <div style={{ fontSize: 12, lineHeight: 1.5, color: "var(--fg-mute)" }}><BidiInline>{n.body}</BidiInline></div>
                {/* a11y: --fg-dim fails 4.5:1 for this small text → --fg-mute. */}
                <div className="mt-1" style={{ fontFamily: "var(--f-mono)", fontSize: 10, color: "var(--fg-mute)" }}>
                  {timeAgo(n.createdAt)}
                </div>
              </div>
            </button>
          ))
        )}
      </div>
      <div className="dropdown-foot">
        <Button variant="ghost" size="sm" disabled={unreadCount === 0} onClick={() => void markAllRead()}>
          {tShell("nav.notif.markAllRead")}
        </Button>
        <Button variant="outline" size="sm" onClick={() => { onClose(); go("inbox"); }}>
          {tShell("nav.notif.openInbox")}
        </Button>
      </div>
    </div>
  );
};

interface UserDropdownProps {
  go: Go;
  role: Role;
  setRole: (id: RoleId) => void;
  auth: AuthContextValue;
  /** R-NAV-1 — the signed-in user's roles, for the multi-role switcher. */
  availableRoles: RoleId[];
}

const UserDropdown = ({ go, role, setRole, auth, availableRoles }: UserDropdownProps): React.ReactElement => {
  const { t: tShell } = useTranslation("shell");
  const [loggingOut, setLoggingOut] = React.useState(false);
  const handleLogout = async (e?: React.MouseEvent): Promise<void> => {
    e?.preventDefault?.();
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      if (auth?.logout) {
        await auth.logout();
      }
    } finally {
      // R-NAV-1: clearing the session drops activeRoleId to null on its
      // own (RoleProvider derives from auth), and its reconcile effect
      // removes the persisted preference. We no longer setRole("student")
      // here — there is no "logged-in student" state to fall back to.
      setLoggingOut(false);
      go("login");
    }
  };

  // Phase-14.8: real authed user only, never the mock identity.
  // - If authenticated: full name → email local-part.
  // - If NOT authenticated: role label ("دانشجو" / "استاد" / …) —
  //   never role.name (which is the mock "نسرین رضوی" for student).
  // - Same for identifier: email if authed, else "مهمان" (guest).
  const isAuthed = !!auth?.user;
  const displayName = isAuthed
    ? (auth.user?.fullName || auth.user?.email?.split("@")[0] || role.label)
    : role.label;
  const identifier = isAuthed ? (auth.user?.email ?? role.label) : tShell("user.guest");
  return (
  <div className="dropdown" style={{ width: 300 }}>
    <div className="p-4.5"  style={{ borderBottom: "1px solid var(--line)"}}>
      <div className="flex items-center gap-3" >
        {/* Phase-A R1.4 B5 — same fix as the trigger above: never leak
            `role.avatar` mock initials to anonymous visitors. Authed →
            the unified deterministic avatar; anonymous → a neutral icon. */}
        {isAuthed && auth.user ? (
          <Avatar
            seed={auth.user.userId}
            role={role.id}
            name={auth.user.fullName || auth.user.email}
            size={44}
          />
        ) : (
          <div className="avatar" style={{ width: 44, height: 44 }} data-anon="true">
            <Icon name="user" size={18} />
          </div>
        )}
        <div className="flex-1"  style={{ minWidth: 0}}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{displayName}</div>
          <div className="mt-0.5"  style={{fontSize: 11, color: "var(--fg-mute)", fontFamily: "var(--f-mono)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>{identifier}</div>
        </div>
      </div>
      {/* R-NAV-1: role label pill (real, derived from the session). The
          old `role.subtitle` pill ("ارشد علوم داده") was baked-in mock
          text identical for every user of a role — dropped. */}
      <div className="flex gap-1.5 mt-3" >
        <span className="pill pill-cyan" style={{ fontSize: 10 }}>{role.label}</span>
      </div>
    </div>

    {/* R-NAV-1 (spec §3.1.6) — multi-role switcher. Replaces the old
        demo «view as any role» mock. Shown ONLY when the signed-in user
        actually holds more than one role; switching is restricted to
        availableRoles, so it can never elevate privilege. */}
    {availableRoles.length > 1 && (
      <div className="p-3.5"  style={{ borderBottom: "1px solid var(--line)", background: "var(--surface-2)"}}>
        <div className="mono mb-2.5"  style={{fontSize: 10, color: "var(--fg-mute)", letterSpacing: "0.1em"}}>
          {tShell("user.switchRole")}
        </div>
        <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${Math.min(availableRoles.length, 5)}, 1fr)` }}>
          {availableRoles.map((rid) => {
            const r = ROLES[rid];
            const isActive = role.id === rid;
            return (
              <button className="rounded-md cursor-pointer flex flex-col items-center gap-1" key={rid} onClick={() => { setRole(rid); go(r.homeRoute); }}
                title={r.label}
                aria-pressed={isActive}
                 style={{padding: "8px 4px",
                  background: isActive ? "var(--accent-soft)" : "var(--surface)",
                  border: "1px solid " + (isActive ? "var(--accent)" : "var(--line)"),
                  fontSize: 10,
                  fontFamily: "inherit",
                  color: isActive ? "var(--accent)" : "var(--fg-mute)"}}>
                <div className={"avatar " + (isActive ? r.color : "")} style={{ width: 22, height: 22, fontSize: 9, opacity: isActive ? 1 : 0.6 }}>{r.avatar}</div>
                {r.label}
              </button>
            );
          })}
        </div>
      </div>
    )}

    <div className="p-1.5" >
      {[
        ["user.item.home", "home", role.homeRoute],
        // Phase B R3.a Commit G (D69) — «پروفایل من» per the D69
        // SelfOrAdmin clarification. Points to /profile, the new R3.a
        // self-service page that works for every authenticated role.
        ["user.item.profile", "user", "profile"],
        ["user.item.messages", "chat", "messages"],
        ["user.item.bookmarks", "star", "bookmarks"],
        ["user.item.achievements", "trophy", "achievements"],
        ["user.item.credential", "cert", "credential"],
        ["user.item.settings", "settings", "settings"],
        ["user.item.help", "headset", "help"],
      ].map(([t, ic, r]) => (
        <button key={t} onClick={() => go(r)} className="dropdown-item">
          <Icon name={ic} size={14} />
          {tShell(t)}
        </button>
      ))}
    </div>
    <div className="p-1.5"  style={{ borderTop: "1px solid var(--line)"}}>
      <button onClick={handleLogout} disabled={loggingOut} className="dropdown-item" style={{ color: "var(--accent)" }}>
        <Icon name="end" size={14} />
        {loggingOut ? tShell("user.loggingOut") : tShell("user.logout")}
      </button>
    </div>
  </div>
  );
};

export interface FooterProps {
  go: Go;
}

// Per-role workspace link in the footer's "برای کاربران" column.
// Phase-14.7 (F-123): the footer used to list every role's workspace
// link (student desk + instructor console + parent portal + admin
// desk) to every visitor, even unauthenticated ones. A student saw
// links to /instructor /admin /parent and clicking them landed on
// the wrong-role page with no permission check. After Phase-14.7
// router.tsx auth-gates workspace routes, but those links still
// shouldn't be visible to the wrong roles — clicking them just
// bounces to /login, which is confusing UX. Now we show ONLY the
// link for the logged-in user's role, and only when authenticated.
// R-NAV-2: each role's primary workspace = its real home route (matches
// role.homeRoute). Footer surfaces only the logged-in user's entry. Mock
// /instructor + /authoring destinations removed; every route here is real
// (faculty-dashboard is a titled ComingSoon placeholder, not a dead link).
const ROLE_WORKSPACE_LINK: Record<RoleId, { route: string; label: string }> = {
  student: { route: "dashboard", label: "home.student" },
  instructor: { route: "progress", label: "home.instructor" },
  admin: { route: "admin", label: "home.admin" },
  finance_manager: { route: "admin/finance/treasury", label: "home.finance_manager" },
  faculty_admin: { route: "faculty-dashboard", label: "home.faculty_admin" },
  parent: { route: "parent", label: "home.parent" },
  org: { route: "org", label: "home.org" },
  ta: { route: "ta", label: "home.ta" },
  content_manager: { route: "content", label: "home.content_manager" },
  support: { route: "support", label: "home.support" },
  moderator: { route: "moderate", label: "home.moderator" },
  super_admin: { route: "super", label: "home.super_admin" },
  // WELL-4 — counselor (مشاور دانشگاه) primary workspace = the counselor inbox.
  counselor: { route: "counselor-inbox", label: "home.counselor" },
  // S-CARD فاز A W1 — تا پیش از کنسولِ عضویت (W5) میزِ کاریِ این دو نقش
  // «پروفایل» است: مسیرِ واقعی و بی‌دروازه، نه لینکِ مرده.
  membership_operator: { route: "profile", label: "home.membership_operator" },
  membership_manager: { route: "profile", label: "home.membership_manager" },
  // S-ASSESS-W3a — تا پیش از کنسولِ ارزیابی (موجِ بعد) میزِ کاریِ این دو نقش
  // «پروفایل» است: مسیرِ واقعی و بی‌دروازه، نه لینکِ مرده.
  assessor: { route: "profile", label: "home.assessor" },
  assessment_manager: { route: "profile", label: "home.assessment_manager" },
};

/**
 * S-LANDING-2 فاز ۲ — شش قابلیتِ شاخص برای ستونِ فوتر. عمداً از
 * `features/features-copy.ts` ایمپورت نمی‌شود: فوتر ایگر است و کشیدنِ
 * کاتالوگِ ۴۸‌تایی به چانکِ ایگر سقفِ باندل را می‌شکند. تستِ
 * `tests/landing/features-catalog.test.ts` بررسی می‌کند هر اسلاگِ این‌جا
 * واقعاً در کاتالوگ وجود دارد، پس واگراییِ خاموش ممکن نیست.
 */
const FOOTER_FEATURE_LINKS: ReadonlyArray<{ slug: string; label: string }> = [
  { slug: "competency-assessment-center", label: "footer.feature.competency-assessment-center" },
  { slug: "live-class", label: "footer.feature.live-class" },
  { slug: "exam-assessment-engine", label: "footer.feature.exam-assessment-engine" },
  { slug: "membership-registry", label: "footer.feature.membership-registry" },
  { slug: "credential-registry-verify", label: "footer.feature.credential-registry-verify" },
  { slug: "knowledge-marketplace", label: "footer.feature.knowledge-marketplace" },
];

export const Footer = ({ go }: FooterProps): React.ReactElement => {
  const { t: tShell } = useTranslation("shell");
  const auth = useAuth();
  const { role } = useRole();
  const isAuthed = auth.isAuthenticated;
  const myWorkspace = ROLE_WORKSPACE_LINK[role.id];
  // Keep brand-name fallback independent from which UI packs are exposed.
  const { locale } = useLocale();
  const brandNameFull = brandNameForLocale(locale);
  return (
  <footer className="footer">
    <div className="shell">
      <div className="footer-grid">
        <div>
          <div className="brand mb-4.5" >
            {/* D52 owner directive «فوت نوت هم بک گراند تیره هست باید لوگو
                روشن بزاره» — فوتر سرمه‌ایِ تیره است، پس نسخهٔ on-dark
                (سفید/طلایی) می‌نشیند؛ همان قاعده با نشانِ EMI موج ۳. */}
            <img
              src={BRAND.assets.markOnDark96}
              alt={brandNameFull}
              width="40"
              height="40"
              loading="lazy"
              decoding="async"
              style={{ width: 40, height: 40, objectFit: "contain", display: "block" }}
            />
            <span>{brandNameFull}
              <div className="brand-sub">{tShell("brand.markSubtitle")}</div>
            </span>
          </div>
          {/* R7.3 A.2.i — drop the inline `color: var(--fg-mute)`.
              The footer-on-dark rule `.footer p { color: rgba(255,255,255,0.7) }`
              gives ~9.66:1 against the navy footer bg (AAA). The
              inline `--fg-mute` (#4a5a76) was 2.77:1 — Lighthouse fail. */}
          <p style={{ fontSize: 14, lineHeight: 1.7, maxWidth: 360 }}>
            {tShell("footer.tagline")}
          </p>
          <div className="standards mt-5" >
            <span className="std">LTI 1.3</span>
            <span className="std">xAPI</span>
            <span className="std">QTI</span>
            <span className="std">Caliper</span>
            <span className="std">WCAG 2.2</span>
          </div>
          <LanguageSwitcher
            authed={isAuthed}
            enabledLocales={auth.user?.enabledLocales}
            variant="footer"
          />
        </div>
        <div>
          {/* R7.3 A.3.ii — footer column headers were `<h5>` directly
              after the page's last `<h2>` (heading-order skip). Renamed
              to `<h3>`; CSS rule `.footer h5` was renamed to `.footer h3`
              in styles.css to preserve the visual treatment. */}
          <h3>{tShell("footer.head.0")}</h3>
          {/* R-NAV-6 (spec §3.8): auth-aware. Anonymous sees ONLY public
              links — never a workspace feature that bounces to /login. */}
          <ul>
            {isAuthed ? (
              <>
                <li><a href="/classroom" onClick={(e)=>{e.preventDefault();go("classroom")}}>{tShell("footer.link.classroom")}</a></li>
                <li><a href="/labs" onClick={(e)=>{e.preventDefault();go("labs")}}>{tShell("footer.link.labs")}</a></li>
                <li><a href="/recordings" onClick={(e)=>{e.preventDefault();go("recordings")}}>{tShell("footer.link.recordings")}</a></li>
                <li><a href="/search" onClick={(e)=>{e.preventDefault();go("search")}}>{tShell("footer.link.search")}</a></li>
                <li><a href="/assessment" onClick={(e)=>{e.preventDefault();go("assessment")}}>{tShell("footer.link.assessment")}</a></li>
              </>
            ) : (
              <>
                <li><a href="/programs" onClick={(e)=>{e.preventDefault();go("programs")}}>{tShell("footer.link.programs")}</a></li>
                <li><a href="/apply" onClick={(e)=>{e.preventDefault();go("apply")}}>{tShell("footer.link.apply")}</a></li>
                <li><a href="/track" onClick={(e)=>{e.preventDefault();go("track")}}>{tShell("footer.link.track")}</a></li>
                <li><a href="/about" onClick={(e)=>{e.preventDefault();go("about")}}>{tShell("footer.link.about")}</a></li>
              </>
            )}
          </ul>
        </div>
        <div>
          <h3>{tShell("footer.head.1")}</h3>
          <ul>
            {isAuthed ? (
              <>
                <li><a href="/schools" onClick={(e)=>{e.preventDefault();go("schools")}}>{tShell("footer.link.schools")}</a></li>
                <li><a href="/faculty" onClick={(e)=>{e.preventDefault();go("faculty")}}>{tShell("footer.link.faculty")}</a></li>
                <li><a href="/research" onClick={(e)=>{e.preventDefault();go("research")}}>{tShell("footer.link.research")}</a></li>
                <li><a href="/library" onClick={(e)=>{e.preventDefault();go("library")}}>{tShell("footer.link.library")}</a></li>
                <li><a href="/events" onClick={(e)=>{e.preventDefault();go("events")}}>{tShell("footer.link.events")}</a></li>
              </>
            ) : (
              <>
                <li><a href="/programs" onClick={(e)=>{e.preventDefault();go("programs")}}>{tShell("footer.link.programs-alt2")}</a></li>
                <li><a href="/about" onClick={(e)=>{e.preventDefault();go("about")}}>{tShell("footer.link.about-alt2")}</a></li>
                <li><a href="/honor-code" onClick={(e)=>{e.preventDefault();go("honor-code")}}>{tShell("footer.link.honor-code")}</a></li>
                <li><a href="/help" onClick={(e)=>{e.preventDefault();go("help")}}>{tShell("footer.link.help")}</a></li>
              </>
            )}
          </ul>
        </div>
        <div>
          <h3>{tShell("footer.head.2")}</h3>
          <ul>
            {isAuthed ? (
              <>
                {/* Phase-14.7: only the logged-in user's own workspace
                    link. Other roles' portals would auth-redirect to
                    /login anyway, so showing them here was misleading. */}
                <li><a href={"/" + myWorkspace.route} onClick={(e)=>{e.preventDefault();go(myWorkspace.route)}}>{tShell(myWorkspace.label)}</a></li>
                <li><a href="/community" onClick={(e)=>{e.preventDefault();go("community")}}>{tShell("footer.link.community")}</a></li>
                <li><a href="/credential" onClick={(e)=>{e.preventDefault();go("credential")}}>{tShell("footer.link.credential")}</a></li>
                <li><a href="/settings" onClick={(e)=>{e.preventDefault();go("settings")}}>{tShell("footer.link.settings")}</a></li>
              </>
            ) : (
              <>
                {/* Unauthenticated visitors see public CTAs, not
                    role-specific portal links. */}
                <li><a href="/login" onClick={(e)=>{e.preventDefault();go("login")}}>{tShell("footer.link.login")}</a></li>
                <li><a href="/apply" onClick={(e)=>{e.preventDefault();go("apply")}}>{tShell("footer.link.apply-alt2")}</a></li>
                <li><a href="/apply" onClick={(e)=>{e.preventDefault();go("apply")}}>{tShell("footer.link.apply")}</a></li>
                <li><a href="/honor-code" onClick={(e)=>{e.preventDefault();go("honor-code")}}>{tShell("footer.link.honor-code-alt2")}</a></li>
              </>
            )}
          </ul>
        </div>
        {/* S-LANDING-2 فاز ۲ — ستونِ «امکانات دانشگاه». مقصدها صفحاتِ شرحِ
            فاز ۱ هستند (`/features/<slug>`)، نه روتِ خودِ محصول: صفحهٔ شرح
            برای مهمان هم باز می‌شود و تصمیمِ «ورود» یا «گفت‌وگو با ما» آن‌جا
            گرفته می‌شود. اسلاگ‌ها این‌جا دستی‌اند تا کاتالوگِ ۴۸‌تایی به چانکِ
            ایگرِ فوتر وارد نشود؛ گاردِ tests/landing وجودِ هرکدام را می‌سنجد. */}
        <div>
          <h3>{tShell("footer.head.3")}</h3>
          <ul>
            {FOOTER_FEATURE_LINKS.map((f) => (
              <li key={f.slug}>
                <a
                  href={"/features/" + f.slug}
                  data-footer-feature={f.slug}
                  onClick={(e)=>{e.preventDefault();go("features", f.slug)}}
                >{tShell(f.label)}</a>
              </li>
            ))}
            <li><a href="/features" onClick={(e)=>{e.preventDefault();go("features")}}>{tShell("footer.link.features")}</a></li>
          </ul>
        </div>
        <div>
          <h3>{tShell("footer.head.4")}</h3>
          <ul>
            {/* «درخواست پذیرش» → the real public /apply funnel. PUBLIC route, so
                it never dead-ends for any visitor (anon or authed) — kept here in
                the always-visible منابع column so it's reachable from every page. */}
            <li><a href="/apply" onClick={(e)=>{e.preventDefault();go("apply")}}>{tShell("footer.link.apply")}</a></li>
            <li><a href="/pricing" onClick={(e)=>{e.preventDefault();go("pricing")}}>{tShell("footer.link.pricing")}</a></li>
            <li><a href="/help" onClick={(e)=>{e.preventDefault();go("help")}}>{tShell("footer.link.help-alt2")}</a></li>
            <li><a href="/about" onClick={(e)=>{e.preventDefault();go("about")}}>{tShell("footer.link.about-alt3")}</a></li>
            {/* دستورِ مالک ۱۴۰۵/۰۵/۱۵ — «یک گزینه» برای معرفیِ مجریِ فنی.
                در ستونِ همیشه-پیدای «منابع» است تا برای مهمان و کاربرِ
                واردشده هر دو از هر صفحه‌ای در دسترس باشد. */}
            <li><a href="/vista" onClick={(e)=>{e.preventDefault();go("vista")}}>{tShell("footer.link.vista")}</a></li>
            {/* R-NAV-6: Office Hours is a workspace route → authed only; anon
                gets the public honor-code instead of a /login bounce. */}
            {isAuthed ? (
              <li><a href="/officehours" onClick={(e)=>{e.preventDefault();go("officehours")}}>Office Hours</a></li>
            ) : (
              <li><a href="/honor-code" onClick={(e)=>{e.preventDefault();go("honor-code")}}>{tShell("footer.link.honor-code")}</a></li>
            )}
            {/* S-LANDING-2 فاز ۲ — این دو ردیف پیش‌تر لینکِ مرده (لنگرِ تهی) بودند:
                «وضعیت سرویس» بدونِ عددِ آپ‌تایمِ واقعی صفحهٔ صادقانه‌ای نمی‌شد و
                «API و توسعه‌دهندگان» مستندِ عمومی‌ای ندارد که به آن لینک شود.
                جایشان چهار صفحهٔ واقعیِ ساخته‌شده در همین بسته نشست. */}
            <li><a href="/faq" onClick={(e)=>{e.preventDefault();go("faq")}}>{tShell("footer.link.faq")}</a></li>
            <li><a href="/contact" onClick={(e)=>{e.preventDefault();go("contact")}}>{tShell("footer.link.contact")}</a></li>
            <li><a href="/terms" onClick={(e)=>{e.preventDefault();go("terms")}}>{tShell("footer.link.terms")}</a></li>
            <li><a href="/privacy" onClick={(e)=>{e.preventDefault();go("privacy")}}>{tShell("footer.link.privacy")}</a></li>
          </ul>
        </div>
      </div>
      {/* S-TRUST-SEALS — نمادهای اعتماد (اینماد + نظام صنفی رایانه‌ای). فوتر
          مشترکِ همهٔ صفحاتِ عمومی است پس این دو نماد روی هر صفحه دیده
          می‌شوند؛ جزئیاتِ هر دو ثابت در trust-seals.tsx. */}
      <TrustSeals />
      {/* Phase-A R1.3: organizational attribution. موج ۳ مالکیت را به سازمان
          نسبت داده بود؛ **مالک در ۱۴۰۵/۰۵/۱۵ تصحیح کرد**: حقِ مادی و معنویِ
          سامانه با مجریِ فنی (ویستا) است. سازمان بهره‌بردار است و نام/نشانش
          می‌ماند. نامِ دارندهٔ حق از `RIGHTS_HOLDER_FA` می‌آید. */}
      <OrgAttribution variant="full" className="footer-org-attribution" />
      {/* S-LAND-POLISH بند ۸ — بلوکِ ترمینالیِ انگلیسیِ پیشین
          («v1.0.0 · build … · region: TEH-01») طبقِ دستورِ مالک برداشته شد:
          روی فوترِ رسمی نه معنایی برای بازدیدکننده داشت و نه با قاعدهٔ
          «UI فارسی‌محور» می‌خواند. جایش خطِ حقوقیِ فارسی می‌نشیند. */}
      <div className="footer-bot">
        <span>{brandNameFull} · {tShell("brand.markSubtitle")}</span>
        <span>© {tShell("brand.copyrightYear")} {RIGHTS_HOLDER_FA} · {tShell("footer.rights")}</span>
      </div>
    </div>
  </footer>
  );
};

// =====================================================
// Persian numerals helper
// =====================================================
// S-I18N-W1a — one digit table, not two: `toFaDigits` picks U+06F0..9 for fa
// and U+0660..9 for ar (STYLE_AR §۳). The Persian output is byte-identical to
// the table this used to inline.
export const toFa = (n: number | string): string => toFaDigits(n);

// R-NAV-5 — relative Persian timestamp for notifications ("۲ ساعت پیش").
// Browser-only (Date.now is fine here; it's unavailable only in workflow
// scripts). Defined module-level; the dropdown calls it lazily at render.
export const timeAgo = (iso: string): string => {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "";
  const diffMin = Math.max(0, Math.floor((Date.now() - then) / 60000));
  if (diffMin < 1) return translate("common:time.justNow", "همین الان");
  // S-I18N-W1a — the four Persian sentences are gone; `Intl.RelativeTimeFormat`
  // knows Arabic's dual and its plural breaks, and with `numeric: "always"` its
  // Persian output is exactly what the hard-coded branches printed. The bucket
  // boundaries below are unchanged, so no row moves to a different unit.
  const h = Math.floor(diffMin / 60);
  const d = Math.floor(h / 24);
  const [value, unit]: [number, Intl.RelativeTimeFormatUnit] =
    diffMin < 60 ? [diffMin, "minute"] : h < 24 ? [h, "hour"] : d < 30 ? [d, "day"] : [Math.floor(d / 30), "month"];
  try {
    return formatLocaleRelativeTime(-value, unit, getLocale(), { numeric: "always" });
  } catch {
    // Preserve timestamp context when this runtime lacks the locale's
    // relative wording; neither default-language copy nor a unitless number.
    return formatLocaleDate(iso, getLocale(), { year: "numeric", month: "numeric", day: "numeric" });
  }
};

// =====================================================
// Sparkline mini-chart (SVG)
// =====================================================
export interface SparklineProps {
  values: number[];
  color?: string;
  height?: number;
  width?: number;
}
export const Sparkline = ({
  values,
  color = "var(--cyan)",
  height = 50,
  width = 220,
}: SparklineProps): React.ReactElement => {
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width;
    const y = height - ((v - min) / range) * height * 0.85 - height * 0.07;
    return [x, y];
  });
  const d = "M " + pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" L ");
  const dArea = d + ` L ${width},${height} L 0,${height} Z`;
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id={`sg-${color.replace(/[^a-z]/gi, "")}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.4" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={dArea} fill={`url(#sg-${color.replace(/[^a-z]/gi, "")})`} />
      <path d={d} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

// =====================================================
// Cognitive radar (SVG) — for student profile
// =====================================================
export interface CognitiveRadarProps {
  values: number[];
  labels: string[];
  size?: number;
}
export const CognitiveRadar = ({ values, labels, size = 280 }: CognitiveRadarProps): React.ReactElement => {
  const cx = size / 2, cy = size / 2;
  const radius = size * 0.4;
  const n = values.length;
  const angle = (i: number): number => (Math.PI * 2 * i) / n - Math.PI / 2;
  const point = (i: number, r: number): [number, number] => [cx + Math.cos(angle(i)) * r, cy + Math.sin(angle(i)) * r];

  const rings = [0.25, 0.5, 0.75, 1].map((f) => {
    const pts = Array.from({ length: n }, (_, i) => point(i, radius * f).join(",")).join(" ");
    return <polygon key={f} points={pts} fill="none" stroke="var(--line-2)" strokeWidth="1" />;
  });
  const spokes = Array.from({ length: n }, (_, i) => {
    const [x, y] = point(i, radius);
    return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="var(--line-2)" strokeWidth="1" />;
  });
  const valuePts = values.map((v, i) => point(i, radius * (v / 100)).join(",")).join(" ");

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {rings}
      {spokes}
      <polygon points={valuePts} fill="oklch(0.82 0.16 195 / 0.18)" stroke="var(--cyan)" strokeWidth="2" strokeLinejoin="round" />
      {values.map((v, i) => {
        const [x, y] = point(i, radius * (v / 100));
        return <circle key={i} cx={x} cy={y} r={3.5} fill="var(--cyan)" />;
      })}
      {labels.map((l, i) => {
        const [x, y] = point(i, radius + 26);
        return (
          <text key={i} x={x} y={y} textAnchor="middle" dominantBaseline="middle"
            fill="var(--fg-mute)" fontSize="11" fontFamily="Vazirmatn, sans-serif" fontWeight="500">
            {l}
          </text>
        );
      })}
    </svg>
  );
};

// =====================================================
// Knowledge Graph visualization (SVG)
// =====================================================
export const KnowledgeGraph = (): React.ReactElement => {
  const { t } = useTranslation(["landing.brand", "landing"], { nsMode: "fallback" });
  const nodes = [
    { id: "ml", x: 50, y: 40, r: 36, kind: "core", key: "ml" },
    { id: "sup", x: 22, y: 22, r: 24, kind: "topic", key: "supervised" },
    { id: "uns", x: 78, y: 22, r: 24, kind: "topic", key: "unsupervised" },
    { id: "rl",  x: 50, y: 76, r: 24, kind: "topic", key: "reinforcement" },
    { id: "lr",  x: 12, y: 50, r: 18, kind: "sub", key: "regression" },
    { id: "svm", x: 28, y: 70, r: 18, kind: "sub", key: "svm" },
    { id: "nn",  x: 88, y: 50, r: 18, kind: "sub", key: "neuralNet" },
    { id: "cl",  x: 70, y: 8,  r: 18, kind: "sub", key: "clustering" },
    { id: "q",   x: 70, y: 92, r: 18, kind: "sub", key: "qLearning" },
  ];
  const edges = [
    ["ml", "sup"], ["ml", "uns"], ["ml", "rl"],
    ["sup", "lr"], ["sup", "svm"], ["sup", "nn"],
    ["uns", "cl"], ["uns", "nn"], ["rl", "q"],
  ];
  const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const colorFor = (kind: string): string =>
    kind === "core" ? "var(--cyan)" : kind === "topic" ? "var(--amber)" : "var(--violet)";

  return (
    <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
      <defs>
        <radialGradient id="node-glow">
          <stop offset="0%" stopColor="var(--cyan)" stopOpacity="0.4" />
          <stop offset="100%" stopColor="var(--cyan)" stopOpacity="0" />
        </radialGradient>
      </defs>
      {edges.map(([a, b], i) => {
        const A = byId[a], B = byId[b];
        return <line key={i} x1={A.x} y1={A.y} x2={B.x} y2={B.y}
          stroke={"color-mix(in oklch, " + colorFor(A.kind) + " 50%, transparent)"}
          strokeWidth="0.3" />;
      })}
      {nodes.map((n) => (
        <g key={n.id}>
          {n.kind === "core" && <circle cx={n.x} cy={n.y} r={n.r / 4} fill="url(#node-glow)" opacity="0.6" />}
          <circle cx={n.x} cy={n.y} r={n.r / 8} fill={colorFor(n.kind)} stroke={colorFor(n.kind)} strokeOpacity="0.4" strokeWidth={n.r / 8} fillOpacity="0.5" />
          <text x={n.x} y={n.y + n.r / 4 + 2} textAnchor="middle" fill="var(--fg)" fontSize="2.2" fontFamily="Vazirmatn">
            {t(`home.knowledgeGraph.nodes.${n.key}`)}
          </text>
        </g>
      ))}
    </svg>
  );
};

// =====================================================
// 3D-ish architecture diagram (isometric stack)
// =====================================================
export const ArchStack = (): React.ReactElement => {
  const { t } = useTranslation(["landing.brand", "landing"], { nsMode: "fallback" });
  const layers = [
    { name: "Experience Layer", sub: t("home.archStack.experienceSub"), color: "var(--cyan)" },
    { name: "Agent Orchestration", sub: "AI Tutor · Coach · Critic · Mentor · Grader", color: "var(--violet)" },
    { name: "Domain Services", sub: "Course · Enrollment · Gradebook · Credential", color: "var(--amber)" },
    { name: "Data & Learning Record Store", sub: "PostgreSQL · ClickHouse · Vector · Object", color: "var(--fg-mute)" },
    { name: "Cloud-Native Platform", sub: "Kubernetes · Kafka · CDN · Observability", color: "var(--fg-dim)" },
  ];
  return (
    <div
      // Phase-16 R11 — the 3D rotation (rotateX(48deg) rotateZ(-28deg))
      // projects the 320-px boxes to a visual width ~360 px, which on
      // 320-px viewports leaked past the page-shell. Clip horizontally
      // at the ArchStack parent so the diagram still renders inside
      // the slot.
      style={{
        perspective: 1400,
        transformStyle: "preserve-3d",
        padding: "20px 0",
        overflowX: "clip",
        maxWidth: "100%",
      }}
    >
      <div
        className="flex flex-col gap-4.5 items-center"
        style={{
          transform: "rotateX(48deg) rotateZ(-28deg)",
          transformStyle: "preserve-3d",
        }}
      >
        {layers.map((L, i) => (
          <div
            className="rounded-xl relative flex flex-col justify-center"
            key={i}
            style={{
              // Cap at 320px on desktop, fluid below — the 3D tilt
              // never visually exceeds the slot now.
              width: "min(320px, calc(100% - 16px))",
              height: 70,
              background: "linear-gradient(180deg, var(--surface-2), var(--surface))",
              border: "1px solid var(--line-2)",
              padding: "0 18px",
              transform: `translateZ(${(layers.length - i) * 20}px)`,
              boxShadow: "0 20px 40px -20px rgba(0,0,0,0.7)",
            }}
          >
            <div
              className="absolute"
              style={{
                top: 8,
                insetInlineEnd: 12,
                width: 5,
                height: 5,
                borderRadius: 50,
                background: L.color,
                boxShadow: `0 0 12px ${L.color}`,
              }}
            />
            <div style={{ fontSize: 14, fontWeight: 600 }}>{L.name}</div>
            <div className="mt-1" style={{ fontFamily: "var(--f-mono)", fontSize: 10, color: "var(--fg-mute)" }}>{L.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// (exports above)
